import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Button from "../../../components/button/Button";
import { Spiner } from "../../../components/spinner/Spiner";
import TableList, { GridCol, GridRow } from "../../../components/table/TableList";
import { auth } from "../../../firebase";
import companiesQuery from "../../../firestore/Companies.query";
import { Company, initialCompany } from "../../../model/Companies";
import { newOnlineUrls, OnlineUrls } from "../../../model/OnlineUrls";
import { OnlineUrlSettingDeleteModal } from "./OnlineUrlSettingDeleteModal";
import { OnlineUrlSettingModal } from "./OnlineUrlSettingModal";
import { AlertValue, alertOpen } from "../../../features/alert.slice";
import { useDispatch } from "react-redux";

export const OnlineUrlSettingPage = () => {

  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;
  // URLパラメータからtenantId取得
  const { tenantId } = useParams<{ tenantId: string }>();

  const dispatch = useDispatch();

  const isDisabled = useRef(false);

  const [isOpen, setIsOpen] = useState(false);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isLoad, setIsLoad] = useState(true);
  const [onlineUrlsList, setOnlineUrlsList] = useState<OnlineUrls[]>([]);
  const [onlineUrl, setOnlineUrl] = useState<OnlineUrls>(newOnlineUrls);
  const [deleteOnlineUrl, setDeleteOnlineUrl] = useState<OnlineUrls | undefined>();
  const [company, setCompany] = useState<Company>(initialCompany);

  useEffect(() => {
    const f = async () => {
      // 返信ボタン一覧取得
      const onlineUrlsList = await companiesQuery.getOnlineUrls(tenantId!);
      setOnlineUrlsList(onlineUrlsList);
      // 団体情報
      const company = await companiesQuery.getCompany(tenantId!);
      setCompany(company);
      setIsLoad(false);
    };
    f();
  }, [isOpen, dialogIsOpen, tenantId]);

  const columns: GridCol[] = [
    {
      field: "number",
      headerName: "No.",
      style: "w-1/12",
    },
    {
      field: "name",
      headerName: "名称",
      style: "",
    },
    {
      field: "type",
      headerName: "作成元",
      style: "",
    },
    {
      field: "control",
      headerName: "操作",
      style: "w-2/12",
    },
  ];

  const rowList: GridRow[] = onlineUrlsList.map((onlineUrls, index) => {
    return {
      rowData: {
        number: index + 1,
        name: (<div className={"sm:whitespace-normal"}>{onlineUrls.name ?? ''}</div>),
        type: onlineUrls.type === "admin" ? ("運営") : company.name,
        control: onlineUrls.type === "admin" ? (
          <>
            <div className="flex">
              <Button
                className="mr-2"
                onClick={() => {
                  setIsUpdate(true);
                  setOnlineUrl(onlineUrls);
                  toggle();
                }}
                children={"編集"}/>
              <Button
                color="red"
                onClick={() => {
                  deleteDialogToggle();
                  setDeleteOnlineUrl(onlineUrls);
                }}
                children={"削除"}/>
            </div>
          </>
        ) : (<></>)
      },
      sortableRowData: {
        name: onlineUrls.name ?? '',
        type: onlineUrls.type === 'admin',
        control: onlineUrls.type === 'admin'
      }
    };
  });

  const onSubmit = async (data: OnlineUrls) => {
    if(!isDisabled.current){
      try {
        isDisabled.current = true;
        const value: OnlineUrls = {
          id: onlineUrl?.id ?? '',
          onlineType: "Zoom",
          name: data.name,
          url: data.url,
          type: "admin",
          createUser: currentUser?.uid!,
          createAt: onlineUrl?.createAt ?? new Date(),
        }
        // ミーティングURL取得
        let onlineUrls =await companiesQuery.getOnlineUrls(tenantId!);
        // 編集対象のミーティングURL以外のミーティングURLを取得
        onlineUrls = onlineUrls.filter(onlineUrl => onlineUrl.id !== data.id)
        // ミーティングURL名称が一致するミーティングURLを取得し重複チェックを行う
        onlineUrls = onlineUrls.filter(onlineUrl => onlineUrl.name === data.name)
        if (onlineUrls.length > 0){
          // 重複した場合、dupCheckをtrueにする
          sessionStorage.setItem("dupCheck", "true");
        }
        // 重複しない場合、 ミーティングURL保存処理実行
        if(sessionStorage.getItem("dupCheck") === "false"){
          if(isUpdate) {
            await companiesQuery.updateOnlineUrl(tenantId!, value);
          } else {
            data.createUser = currentUser?.uid!;
            data.createAt = new Date();
            await companiesQuery.setOnlineUrl(tenantId!, value);
          }
          dispatch(alertOpen(AlertValue("URLを保存しました", "success")));
          toggle();
        }
      } catch (err: any) {
        let errorMessage = err.code;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(alertOpen(AlertValue("ネットワークに接続できません。", "danger")));
        } else {
          dispatch(alertOpen(AlertValue("保存に失敗しました", "danger")));
        }
        console.error(err);
      } finally {
        isDisabled.current = false;
      }
    }
  };

  const toggle = () => {
    setIsOpen(!isOpen);
  };
  const deleteDialogToggle = () => {
    setDialogIsOpen(!dialogIsOpen);
  };

  const onDelete = (onlineUrlsId: string) => {
    if(!isDisabled.current){
      try {
        isDisabled.current = true;
        deleteDialogToggle();
        companiesQuery.deleteOnlineUrl(tenantId!, onlineUrlsId);
        dispatch(alertOpen(AlertValue("削除しました。", "success")));
        isDisabled.current = false;
      } catch (err: any) {
        console.error(err);
        dispatch(alertOpen(AlertValue("削除に失敗しました", "danger")));
        isDisabled.current = false;
      }
    }
  }

  return (
    <div className="h-full">
      <OnlineUrlSettingModal
        children={undefined}
        open={isOpen}
        toggle={toggle}
        closeOnClickOutside={false}
        updateFlg={isUpdate}
        onlineUrl={onlineUrl}
        authorUid={currentUser?.uid!}
        onSubmit={onSubmit}
      />
      <OnlineUrlSettingDeleteModal
        children={undefined}
        open={dialogIsOpen}
        toggle={deleteDialogToggle}
        closeOnClickOutside={false}
        onlineUrl={deleteOnlineUrl}
        handle={onDelete}
      />
      <div className="flex items-center space-x-4 p-6 justify-between">
        <h2 className="flex text-xl whitespace-nowrap font-semibold text-gray-900">
          ミーティングURL一覧
        </h2>
        <div className={"flex space-x-2 items-center"}>
          <Button
            size={"sm"}
            onClick={() => {
              setIsUpdate(false);
              setOnlineUrl(newOnlineUrls);
              toggle();
            }}
            children={"ミーティングURL新規登録"}
          />
        </div>
      </div>
      {isLoad === true ? <Spiner /> :(
        <div className={"px-6"}>
          <TableList
            header
            gridCol={columns}
            gridRow={rowList}
            defaultSortOrder={"desc"}
            limit={50}
          />
        </div>
      )}
    </div>
  );
}